import type { ThemePreference } from '@markbird/shared';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';
import type { LocalePref } from '../i18n';
import { BackupSettings } from './BackupSettings';
import { FolderSyncSettings } from './FolderSyncSettings';
import { GithubSyncSettings } from './GithubSyncSettings';
import { StorageSettings } from './StorageSettings';
import { VaultSettings } from './VaultSettings';
import { CustomCssSettings } from './CustomCssSettings';
import { PeriodicSettings } from './PeriodicSettings';
import {
  PAGE_MARGINS,
  PAGE_SIZES,
  type PageMargins,
  type PageSize,
} from '../lib/export/printLayout';
import { Icon } from './Icon';
import { Modal } from './Modal';

const THEMES: ThemePreference[] = ['system', 'light', 'dark'];
const THEME_KEY: Record<ThemePreference, string> = {
  system: 'theme.auto',
  light: 'theme.light',
  dark: 'theme.dark',
};
const LOCALES: LocalePref[] = ['auto', 'en', 'de'];
const LOCALE_KEY: Record<LocalePref, string> = {
  auto: 'lang.auto',
  en: 'lang.en',
  de: 'lang.de',
};

/** Local editor settings: theme, language, font size, line numbers, Vim, typewriter. */
export function SettingsDialog() {
  const t = useT();
  const open = useStore((s) => s.settingsOpen);
  const setOpen = useStore((s) => s.setSettingsOpen);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const fontSize = useStore((s) => s.editorFontSize);
  const setFontSize = useStore((s) => s.setEditorFontSize);
  const lineNumbers = useStore((s) => s.lineNumbers);
  const setLineNumbers = useStore((s) => s.setLineNumbers);
  const vimMode = useStore((s) => s.vimMode);
  const toggleVim = useStore((s) => s.toggleVim);
  const typewriter = useStore((s) => s.typewriter);
  const toggleTypewriter = useStore((s) => s.toggleTypewriter);
  const locale = useStore((s) => s.locale);
  const setLocale = useStore((s) => s.setLocale);
  const exportTheme = useStore((s) => s.exportTheme);
  const setExportTheme = useStore((s) => s.setExportTheme);
  const exportPageSize = useStore((s) => s.exportPageSize);
  const setExportPageSize = useStore((s) => s.setExportPageSize);
  const exportMargins = useStore((s) => s.exportMargins);
  const setExportMargins = useStore((s) => s.setExportMargins);
  const exportToc = useStore((s) => s.exportToc);
  const setExportToc = useStore((s) => s.setExportToc);

  if (!open) return null;

  return (
    <Modal onClose={() => setOpen(false)} title={t('settings.title')}>
      <div className="mb-setting">
        <label>{t('settings.theme')}</label>
        <div className="mb-switch-tabs" role="group" aria-label={t('settings.theme')}>
          {THEMES.map((th) => (
            <button key={th} aria-pressed={theme === th} onClick={() => setTheme(th)}>
              {t(THEME_KEY[th])}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-language">{t('settings.language')}</label>
        <select
          id="mb-language"
          value={locale}
          onChange={(e) => setLocale(e.target.value as LocalePref)}
        >
          {LOCALES.map((l) => (
            <option key={l} value={l}>
              {t(LOCALE_KEY[l])}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-fontsize">{t('settings.fontSize')}</label>
        <div className="mb-stepper">
          <button aria-label={t('settings.decrease')} onClick={() => setFontSize(fontSize - 1)}>
            <Icon name="minus" size={14} />
          </button>
          <span id="mb-fontsize">{fontSize}px</span>
          <button aria-label={t('settings.increase')} onClick={() => setFontSize(fontSize + 1)}>
            <Icon name="plus" size={14} />
          </button>
        </div>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-linenums">{t('settings.lineNumbers')}</label>
        <input
          id="mb-linenums"
          type="checkbox"
          checked={lineNumbers}
          onChange={(e) => setLineNumbers(e.target.checked)}
        />
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-vim">{t('settings.vim')}</label>
        <input id="mb-vim" type="checkbox" checked={vimMode} onChange={() => toggleVim()} />
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-typewriter">{t('settings.typewriter')}</label>
        <input
          id="mb-typewriter"
          type="checkbox"
          checked={typewriter}
          onChange={() => toggleTypewriter()}
        />
      </div>

      <div className="mb-setting">
        <label>{t('settings.exportTheme')}</label>
        <div className="mb-switch-tabs" role="group" aria-label={t('settings.exportTheme')}>
          <button aria-pressed={exportTheme === 'light'} onClick={() => setExportTheme('light')}>
            {t('theme.light')}
          </button>
          <button aria-pressed={exportTheme === 'dark'} onClick={() => setExportTheme('dark')}>
            {t('theme.dark')}
          </button>
        </div>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-export-pagesize">{t('settings.pageSize')}</label>
        <select
          id="mb-export-pagesize"
          className="mb-sort-select"
          value={exportPageSize}
          onChange={(e) => setExportPageSize(e.target.value as PageSize)}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {t(`settings.pageSize.${size}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-export-margins">{t('settings.margins')}</label>
        <select
          id="mb-export-margins"
          className="mb-sort-select"
          value={exportMargins}
          onChange={(e) => setExportMargins(e.target.value as PageMargins)}
        >
          {PAGE_MARGINS.map((m) => (
            <option key={m} value={m}>
              {t(`settings.margins.${m}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-setting">
        <label htmlFor="mb-export-toc">{t('settings.exportToc')}</label>
        <input
          id="mb-export-toc"
          type="checkbox"
          checked={exportToc}
          onChange={(e) => setExportToc(e.target.checked)}
        />
      </div>

      <PeriodicSettings />

      <CustomCssSettings />

      <StorageSettings />

      <VaultSettings />

      <FolderSyncSettings />
      <GithubSyncSettings />

      <BackupSettings />

      <div className="mb-modal-actions">
        <button className="mb-btn mb-btn-primary" onClick={() => setOpen(false)}>
          {t('common.done')}
        </button>
      </div>
    </Modal>
  );
}
